import React, { useState, useEffect, useRef } from "react";
import { getPincodeDetails } from "../Service/lookup";
import { searchPlaces, getPlaceDetails } from "../Service/OlaMapsService";

const AddressDetailForm = ({
  addressLine1,
  setAddressLine1,
  addressLine2,
  setAddressLine2,
  state,
  setState,
  city,
  setCity,
  pinCode,
  setPinCode,
  country,
  setCountry,
  stateList = [],
  districtList = [],
  role,
  setCoords,
  setFullAddress,
}) => {
  const [localPinCode, setLocalPinCode] = useState(pinCode);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const suggestionRef = useRef(null);

  useEffect(() => {
    setLocalPinCode(pinCode);
  }, [pinCode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePinCodeChange = (e) => {
    const value = e.target.value;
    if (/^\d{0,6}$/.test(value)) {
      setLocalPinCode(value);
    }
  };

  const handlePinCodeBlur = async () => {
    setPinCode(localPinCode);

    // Fetch address details if pincode is 6 digits
    if (localPinCode.length === 6) {
      try {
        const response = await getPincodeDetails(localPinCode);
        console.log("Pincode API Response:", response);

        if (response && response.error === false) {
          // Update state based on API response
          if (response.data.state) {
            setState(response.data.state);
          }

          // Priority: district > city (district is usually the city/main area)
          if (response.data.district) {
            setCity(response.data.district);
          } else if (response.data.city) {
            setCity(response.data.city);
          }
        }
      } catch (error) {
        console.error("Error fetching pincode details:", error);
      }
    }
  };

  const onAddressChange = async (e) => {
    const query = e.target.value;
    setAddressLine1(query);

    if (query.length >= 3) {
      setIsSearching(true);
      try {
        const results = await searchPlaces(query);
        setSuggestions(results);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Error searching places:", error);
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
    }
  };

  const handleSelectSuggestion = async (suggestion) => {
    setShowSuggestions(false);
    setIsLoadingDetails(true);

    try {
      const details = await getPlaceDetails(suggestion.place_id);

      console.log("Place Details:", details);

      if (details) {
        // Prefill address components
        const components = details.address_components || [];
        let locality = "";
        let sublocality = "";
        let postalTown = "";
        let administrativeAreaLevel2 = "";
        let administrativeAreaLevel1 = "";
        let countryName = "";
        let postalCode = "";

        components.forEach((component) => {
          const types = component.types;
          if (types.includes("locality")) locality = component.short_name;
          if (types.includes("sublocality")) sublocality = component.short_name;
          if (types.includes("postal_town")) postalTown = component.short_name;
          if (types.includes("administrative_area_level_2"))
            administrativeAreaLevel2 = component.short_name;
          if (types.includes("administrative_area_level_1"))
            administrativeAreaLevel1 = component.short_name;
          if (types.includes("country")) countryName = component.short_name;
          if (types.includes("postal_code")) postalCode = component.short_name;
        });

        // Refinement: city should specifically be 'locality'
        const cityVal = locality;

        // Refinement: address1 = name, sublocality, postal_town
        const nameVal = details.name || "";
        const addressParts = [nameVal, sublocality, postalTown].filter(Boolean);
        const concatenatedAddress = addressParts.join(", ");

        setAddressLine1(
          concatenatedAddress || details.description || suggestion.description
        );
        if (cityVal) setCity(cityVal);
        if (administrativeAreaLevel1) setState(administrativeAreaLevel1);
        if (postalCode) {
          setPinCode(postalCode);
          setLocalPinCode(postalCode);
        }
        if (countryName) setCountry(countryName);

        if (setCoords && details.geometry && details.geometry.location) {
          setCoords(details.geometry.location);
        }
        if (setFullAddress && details.formatted_address) {
          setFullAddress(details.formatted_address);
        }
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  return (
    <div className="mb-8">
      <h3 className="text-sm font-semibold mb-2 font-poppins text-[#3B3B3B]">
        {role} Address Details
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="relative md:col-span-2" ref={suggestionRef}>
          <p className="mb-1 text-[13px] text-[#606060] font-poppins">
            Address Line 1 <span className="text-[red]">*</span>
          </p>
          <div className="relative">
            <input
              type="text"
              value={addressLine1}
              onChange={onAddressChange}
              placeholder="Search address..."
              className="border border-[#D2D2D2] text-[12px] p-1.5 pr-8 rounded-sm w-full font-poppins focus:outline-none focus:border-[#4951EC]"
              autoComplete="off"
              disabled={isLoadingDetails}
            />
            {(isSearching || isLoadingDetails) && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>

          {showSuggestions && suggestions.length > 0 && !isSearching && (
            <div className="absolute z-50 w-full bg-white border border-[#D2D2D2] mt-1 rounded-sm shadow-lg max-h-60 overflow-y-auto">
              {suggestions.map((item, index) => (
                <div
                  key={index}
                  className="p-2 hover:bg-gray-100 cursor-pointer text-[12px] font-poppins border-b last:border-b-0"
                  onClick={() => handleSelectSuggestion(item)}
                >
                  {item.description}
                </div>
              ))}
            </div>
          )}

          {showSuggestions && isSearching && (
            <div className="absolute z-50 w-full bg-white border border-[#D2D2D2] mt-1 rounded-sm shadow-lg p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                <span className="text-[12px] text-gray-600 font-poppins">
                  Searching...
                </span>
              </div>
            </div>
          )}

          {showSuggestions && !isSearching && suggestions.length === 0 && (
            <div className="absolute z-50 w-full bg-white border border-[#D2D2D2] mt-1 rounded-sm shadow-lg p-3 text-center">
              <span className="text-[12px] text-gray-500 font-poppins">
                No results found
              </span>
            </div>
          )}
        </div>

        <div className="md:col-span-1">
          <p className="mb-1 text-[13px] text-[#606060] font-poppins">
            Address Line 2{" "}
            <span className="text-[10px] text-[#5B60A8]">(optional)</span>
          </p>
          <input
            type="text"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            placeholder=""
            className="border border-[#D2D2D2] text-[12px] p-1.5 rounded-sm w-full font-poppins focus:outline-none focus:border-[#4951EC]"
            disabled={isLoadingDetails}
          />
        </div>

        <div>
          <p className="mb-1 text-[13px] text-[#606060] font-poppins">
            Pin Code <span className="text-[red]">*</span>
          </p>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={localPinCode}
            onChange={handlePinCodeChange}
            onBlur={handlePinCodeBlur}
            placeholder=""
            className="border border-[#D2D2D2] text-[12px] p-1.5 rounded-sm w-full font-poppins focus:outline-none focus:border-[#4951EC]"
            disabled={isLoadingDetails}
          />
        </div>

        <div>
          <p className="mb-1 text-[13px] text-[#606060] font-poppins">
            State <span className="text-[red]">*</span>
          </p>
          <input
            type="text"
            value={state}
            readOnly
            className="w-full text-[12px] text-[#606060] font-poppins border border-[#D2D2D2] px-2 py-1.5 rounded-sm bg-gray-50 cursor-not-allowed"
            placeholder=""
          />
        </div>

        <div>
          <p className="mb-1 text-[13px] text-[#606060] font-poppins">
            City <span className="text-[red]">*</span>
          </p>
          <input
            type="text"
            value={city}
            readOnly
            className="w-full text-[12px] text-[#606060] font-poppins border border-[#D2D2D2] px-2 py-1.5 rounded-sm bg-gray-50 cursor-not-allowed"
            placeholder=""
          />
        </div>
      </div>
    </div>
  );
};

export default AddressDetailForm;
